import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { APP_PROJECTS } from '../../data/apps';
import { useLanguage } from '../../i18n';

const Page = styled.div`
  padding-top: 110px; /* 兼容固定 Header 高度 */
  padding-bottom: 80px;
  background: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Title = styled.h1`
  font-size: 40px;
  font-weight: 800;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.7;
  font-size: 16px;
  margin-bottom: 32px;
  max-width: 760px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.button)`
  text-align: left;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: 22px;
  background: ${props => props.theme.colors.white};
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: ${props => props.theme.shadows.sm};

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: rgba(59, 130, 246, 0.35);
  }
`;

const CardTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 10px;
`;

const CardDesc = styled.div`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.7;
  font-size: 14px;
  margin-bottom: 14px;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.1);
  color: ${props => props.theme.colors.primaryDark};
  border: 1px solid rgba(59, 130, 246, 0.2);
`;

const AppFactoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <Page>
      <Container>
        <Title>{t('apps.factory.title')}</Title>
        <Subtitle>{t('apps.factory.subtitle')}</Subtitle>

        <Grid>
          {APP_PROJECTS.map(app => (
            <Card
              key={app.slug}
              onClick={() => navigate(`/apps/${app.slug}`)}
              whileTap={{ scale: 0.98 }}
              aria-label={`${t('apps.factory.open')} ${app.name}`}
            >
              <CardTitle>{app.name}</CardTitle>
              <CardDesc>{app.shortDescription}</CardDesc>
              {!!app.tags?.length && (
                <Tags>
                  {app.tags.slice(0, 6).map(tag => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </Tags>
              )}
            </Card>
          ))}
        </Grid>
      </Container>
    </Page>
  );
};

export default AppFactoryPage;

